import React, { useState, useEffect } from "react";
import { Box, Card, Typography, Grid } from "@mui/material";
import { Pie, Bar, Line } from "react-chartjs-2";
import axios from "axios";
import api, { API_BASE_URL } from "../../utils/api";
import { processMonthlyData } from "./utils";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const softColors = [
  "#A3E4D7", "#F9E79F", "#AED6F1", "#F5B7B1", "#D2B4DE",
  "#A9CCE3", "#FAD7A0", "#A2D9CE", "#F5CBA7", "#BB8FCE"
];

const ArchitecturalRequestsTab = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [typeMap, setTypeMap] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reqRes, typeRes] = await Promise.all([
        api.get(`/architectural-requests`),
        api.get(`/architectural-request-types`),
      ]);

      setRequests(reqRes.data);

      const map = {};
      typeRes.data.forEach(t => {
        map[t.id] = t.type;
      });
      setTypeMap(map);
    } catch (error) {
      console.error("Error fetching architectural data:", error);
    } finally {
      setLoading(false);
    }
  };

  const total = requests.length;

  // 🛠 Correct Status Counts
  const open = requests.filter(req => {
    const status = req.status?.toLowerCase();
    return status !== "approved" && status !== "completed";
  }).length;
  
  const approved = requests.filter(req => req.status?.toLowerCase() === "approved").length;
  const completed = requests.filter(req => req.status?.toLowerCase() === "completed").length;

  const avgCompletionDays = (() => {
    const completedReqs = requests.filter(req => req.resolved_at && req.requested_at);
    if (completedReqs.length === 0) return 0;
    const totalDays = completedReqs.reduce((sum, req) => {
      const created = new Date(req.requested_at);
      const resolved = new Date(req.resolved_at);
      return sum + (resolved - created) / (1000 * 60 * 60 * 24);
    }, 0);
    return (totalDays / completedReqs.length).toFixed(1);
  })();

  const monthlyCreated = processMonthlyData(requests, "requested_at");

  const statusCounts = requests.reduce((acc, req) => {
    const status = req.status || "Unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  const statusChartData = {
    labels: Object.keys(statusCounts),
    datasets: [{
      data: Object.values(statusCounts),
      backgroundColor: softColors,
    }],
  };

  const priorityCounts = requests.reduce((acc, req) => {
    const priority = req.priority || "Unknown";
    acc[priority] = (acc[priority] || 0) + 1;
    return acc;
  }, {});
  const priorityChartData = {
    labels: Object.keys(priorityCounts),
    datasets: [{
      data: Object.values(priorityCounts),
      backgroundColor: softColors,
    }],
  };

  const typeCounts = requests.reduce((acc, req) => {
    const typeName = typeMap[req.request_type] || "Unknown Type";
    acc[typeName] = (acc[typeName] || 0) + 1;
    return acc;
  }, {});
  const topTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const topTypesChartData = {
    labels: topTypes.map(t => t[0]),
    datasets: [{
      label: "Requests",
      data: topTypes.map(t => t[1]),
      backgroundColor: softColors,
    }],
  };

  const monthlyCreatedChartData = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    datasets: [{
      label: "Architectural Requests",
      data: monthlyCreated,
      backgroundColor: "rgba(33, 150, 243, 0.5)",
      borderColor: "rgba(33, 150, 243, 1)",
      fill: true,
    }],
  };

  return (
    <Box sx={{ paddingBottom: 5 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        🏛️ Architectural Requests Overview
      </Typography>

      {/* 🧠 KPIs Corrected */}
      <Grid container spacing={2} sx={{ marginBottom: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ padding: 3, textAlign: "center" }}>
            <Typography variant="h6">Total Requests</Typography>
            <Typography variant="h4">{total}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ padding: 3, textAlign: "center" }}>
            <Typography variant="h6">Open</Typography>
            <Typography variant="h4">{open}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ padding: 3, textAlign: "center" }}>
            <Typography variant="h6">Approved</Typography>
            <Typography variant="h4">{approved}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ padding: 3, textAlign: "center" }}>
            <Typography variant="h6">Completed</Typography>
            <Typography variant="h4">{completed}</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* 📈 Pie Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ padding: 3 }}>
            <Typography variant="h6" gutterBottom>Requests by Status</Typography>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300, maxWidth: 400, margin: "0 auto" }}>
              {loading ? <Typography>Loading...</Typography> :
                <Pie
                  data={statusChartData}
                  options={{
                    plugins: { legend: { position: 'right' } },
                    maintainAspectRatio: false,
                  }}
                />}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ padding: 3 }}>
            <Typography variant="h6" gutterBottom>Requests by Priority</Typography>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300, maxWidth: 400, margin: "0 auto" }}>
              {loading ? <Typography>Loading...</Typography> :
                <Pie
                  data={priorityChartData}
                  options={{
                    plugins: { legend: { position: 'right' } },
                    maintainAspectRatio: false,
                  }}
                />}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* 📈 Line and Bar in one card */}
      <Box sx={{ marginTop: 4 }}>
        <Card sx={{ padding: 4 }}>
          <Typography variant="h6" gutterBottom>Trends & Top Types</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Requests Created per Month
              </Typography>
              {loading ? <Typography>Loading...</Typography> : <Line data={monthlyCreatedChartData} />}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Top 5 Request Types
              </Typography>
              {loading ? <Typography>Loading...</Typography> : <Bar data={topTypesChartData} />}
            </Grid>
          </Grid>
        </Card>
      </Box>
    </Box>
  );
};

export default ArchitecturalRequestsTab;
